import {
  AudienceIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  SupportRadioIcon,
  SupportTvIcon,
  Typography,
  ValorationIcon,
} from '@acceso/design-system';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ItemList } from '@/components/ItemList';
import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import { useGetClipsCategories } from '@/features/clips/api/get-clip-categories';
import { TClip } from '@/features/clips/types';
import { useProgramsByIds } from '@/features/programs/hooks/useProgramsByIds';
import { formatDateCustom } from '@/helpers/format-date';

type ClipAdditionalInfoDetailProps = {
  item: TClip | null;
};

export function ClipAdditionalInfoDetail({ item }: ClipAdditionalInfoDetailProps) {
  const { t } = useTranslation();
  const { programsById } = useProgramsByIds([item?.program_id]);
  const { data: categories = [] } = useGetClipsCategories();

  const selectedProgram = item?.program_id ? programsById.get(item.program_id) : undefined;

  const selectedCategory = useMemo(
    () => categories.find(category => category.id === item?.clip_category_id),
    [categories, item?.clip_category_id]
  );

  if (!item) return null;

  return (
    <div className="flex flex-1 flex-col items-start self-stretch">
      <div className="flex w-full flex-col divide-y divide-stroke-20">
        <ItemList title={t('Emisora')}>
          <div className="flex items-center gap-1">
            <img
              src={selectedProgram?.channel?.parameters?.logo_presigned_url}
              alt="Logo"
              className="h-8 w-8 rounded-full object-contain"
            />
            <div className="flex flex-col gap-1">
              <Typography
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Semibold}
                color={FontColorEnum.Dark10}
              >
                {selectedProgram?.channel?.name}
              </Typography>
              <Typography
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Regular}
                color={FontColorEnum.Medium}
              >
                {selectedProgram?.channel?.short_name}
              </Typography>
            </div>
          </div>
        </ItemList>
        <ItemList title={t('Programa')}>
          <Typography fontSize={FontSizeEnum.Md} color={FontColorEnum.Medium}>
            {selectedProgram?.name}
          </Typography>
        </ItemList>
        <ItemList title={t('Fecha y hora')}>
          <div className="flex flex-col gap-1">
            <Typography
              fontSize={FontSizeEnum.Basic}
              weight={FontWeightEnum.Semibold}
              color={FontColorEnum.Dark10}
            >
              {formatDateCustom({ input: item.start_datetime, format: 'dd/MM/yyyy' })}
            </Typography>
            <Typography fontSize={FontSizeEnum.Basic} color={FontColorEnum.Dark10}>
              {formatDateCustom({ input: item.start_datetime, format: 'HH:mm' })} -
              {formatDateCustom({ input: item.end_datetime, format: 'HH:mm' })}
            </Typography>
          </div>
        </ItemList>
        <ItemList title={t('Soporte')}>
          {selectedProgram?.channel?.media_type.includes(ChannelMediaTypeEnum.TV) ? (
            <div className="flex items-center gap-1">
              <SupportTvIcon />
              <Typography
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Semibold}
                color={FontColorEnum.Dark10}
              >
                {t('Televisión')}
              </Typography>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <SupportRadioIcon />
              <Typography
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Semibold}
                color={FontColorEnum.Dark10}
              >
                {t('Radio')}
              </Typography>
            </div>
          )}
        </ItemList>
        <ItemList title={t('Datos')}>
          <div className="flex gap-2">
            <div className="flex items-center gap-1">
              <AudienceIcon />
              <Typography
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Semibold}
                color={FontColorEnum.Dark10}
              >
                {`${item.audience || '-'} / ${item.audience_rating_percent ? item.audience_rating_percent.toFixed(2) : '0.00'} %`}
              </Typography>
            </div>

            <div className="flex items-center gap-1">
              <ValorationIcon />
              <Typography
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Semibold}
                color={FontColorEnum.Dark10}
              >
                {item.economical_value || '-'}
              </Typography>
            </div>
          </div>
        </ItemList>
        <ItemList title={t('Temática')}>
          <Typography fontSize={FontSizeEnum.Md} color={FontColorEnum.Medium}>
            {selectedCategory?.name}
          </Typography>
        </ItemList>
      </div>
    </div>
  );
}
